<template>
  <div
      class="setting-field">
    <!-- MINI -->
    <AerialZonesCanvas
        v-if="ready"
        ref="miniCanvas"
        :zones="view.preview.zones"
        :config="view.preview.config" />

    <!-- ZOOM IN -->
    <el-dialog
        class="motion-zones-dialog"
        width="82%"
        title="Bird's Eye Zones"
        :visible.sync="zoomIn">
      <div class="grid motion-zones-dialog__content">
        <div
            class="span-3 flex-row"
            style="margin-right: 10px;">
          <div>
            <el-form
                v-if="view.data.selectedZone"
                ref="form"
                :model="view.data.selectedZone"
                :rules="view.data.rules"
                label-position="top"
                label-width="170px">
              <el-form-item
                  label="Zone"
                  size="mini">
                <el-select
                    v-model="view.data.selectedZoneUniqueId"
                    placeholder="Select Zone"
                    size="small"
                    @change="selectedZoneChanged()">
                  <el-option
                      v-for="(zone, index) in view.data.zones.filter(zone => zone.drawn)"
                      :key="zone.uniqueId"
                      :label="'[' + (index+1).toString() + '] ' + zone.name"
                      :value="zone.uniqueId" />
                </el-select>
              </el-form-item>

              <el-form-item
                  label="Name"
                  prop="name"
                  size="mini">
                <el-input
                    v-model="view.data.selectedZone.name"
                    size="small"
                    @change="selectedZoneDataChanged()" />
              </el-form-item>

              <el-form-item
                  label="Active"
                  prop="enabled"
                  size="mini">
                <el-switch
                    v-model="view.data.selectedZone.enabled"
                    @change="selectedZoneDataChanged()" />
              </el-form-item>

              <el-form-item
                  label="Detection Types"
                  prop="properties.detection_types"
                  size="mini">
                <el-select
                    v-model="view.data.selectedZone.properties.detection_types"
                    multiple
                    filterable
                    allow-create
                    size="small"
                    @change="selectedZoneDataChanged()">
                  <el-option
                      v-for="detectionType in view.data.detectionTypes"
                      :key="detectionType"
                      :label="detectionType"
                      :value="detectionType" />
                </el-select>
              </el-form-item>
            </el-form>
          </div>
        </div>
        <AerialZonesCanvas
            ref="zoomInCanvas"
            class="span-9 zoom-in-zones-wrapper"
            :zones="view.data.zones"
            :config="view.data.config" />
      </div>
      <div
          slot="footer">
        <el-row
            type="flex"
            style="height: 46px;">
          <el-col
              :span="12"
              style="text-align: left; padding: 0px; border: initial;">
            <el-button
                :disabled="!canAddZones()"
                @click="addZone()">
              Add Zone
            </el-button>
            <el-button
                :disabled="!view.data.selectedZone"
                type="danger"
                @click="removeZone()">
              Delete Zone
            </el-button>
          </el-col>
          <el-col
              :span="12"
              style="padding: 0px; border: initial;">
            <el-button
                type="primary"
                @click="saveData()">
              Save
            </el-button>
            <el-button @click="zoomIn = false">
              Close
            </el-button>
          </el-col>
        </el-row>
      </div>
    </el-dialog>

    <div style="padding: 10px; text-align: right">
      <el-button
          type="primary"
          size="mini"
          circle
          @click="zoomIn = true">
        <i
            class="el-icon-zoom-in"
            style="font-size: x-large"></i>
      </el-button>
    </div>
  </div>
</template>

<script>
import { get, merge } from "lodash";
import AerialZonesHelper from "./AerialZones/AerialZonesHelper";
import AerialZonesCanvas from "./AerialZones/AerialZonesCanvas";

import { createNamespacedHelpers } from "vuex";
const { mapGetters, mapActions, mapMutations } =
  createNamespacedHelpers("db/device");

let wantedAspect = { width: 900, height: 400 };

const calculateArea = (parent, proportion = 1) => ({
  width: parent.width * proportion,
  height:
    ((parent.width * wantedAspect.height) / wantedAspect.width) * proportion,
});

export default {
  name: "AerialZones",
  components: {
    AerialZonesCanvas,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      zoomIn: false,
      ready: false,
      view: {
        preview: {
          config: {
            width: null,
            height: null,
            opacity: 1,
            draggable: false,
          },
          zones: [],
        },
        data: {
          selectedZone: null,
          selectedZoneUniqueId: null,
          config: {
            width: null,
            height: null,
            opacity: 1,
            draggable: false,
          },
          zones: [],
          rules: {
            name: [
              {
                required: true,
                message: "Name can't be blank",
                trigger: "blur",
              },
            ],
          },
          detectionTypes: [],
        },
      },
      data: {},
    };
  },
  computed: {
    ...mapGetters(["doorbotId", "device"]),
  },
  watch: {
    "view.data.zones": {
      handler() {
        this.verifySelectedZone();
      },
      deep: true,
    },
  },
  mounted() {
    this.data = this.config.value;
    this.configPreview();
    this.configData();
    this.ready = true;
    this.configDetectionTypes();
  },
  methods: {
    ...mapActions(["updateDevice", "updateDeviceZones"]),
    ...mapMutations(["updateDeviceZoneSettings"]),
    configPreview() {
      const parentDimensions = this.$parent.$el.getBoundingClientRect();
      const dimensions = calculateArea(parentDimensions, 0.9);
      this.view.preview.config.width = dimensions.width;
      this.view.preview.config.height = dimensions.height;
      this.view.preview.config.opacity = 0.8;
      this.view.preview.config.draggable = false;

      this.$nextTick(() => {
        const stage = this.$refs.miniCanvas.$refs.stage.getStage();
        const options = {
          width: stage.width(),
          height: stage.height(),
          opacity: 0.8,
          draggable: false,
        };
        this.view.preview.zones = AerialZonesHelper.buildZonesForUI(
          this.data,
          options
        );
      });
    },
    configData() {
      const dimensions = calculateArea(wantedAspect, 1);
      this.view.data.config.width = dimensions.width;
      this.view.data.config.height = dimensions.height;
      this.view.data.config.opacity = 0.8;
      this.view.data.config.draggable = true;

      const options = {
        width: wantedAspect.width,
        height: wantedAspect.height,
        opacity: 0.8,
        draggable: true,
      };
      this.view.data.zones = AerialZonesHelper.buildZonesForUI(
        this.data,
        options
      );

      this.verifySelectedZone();
    },
    configDetectionTypes() {
      this.view.data.detectionTypes = Object.keys(
        get(
          this.device,
          `${this.device.type}_firmware_settings.cv_settings.detection_types`,
          {}
        )
      );
    },
    selectedZoneChanged() {
      if (this.view.data.selectedZone)
        this.view.data.selectedZone.selected = false;
      this.view.data.selectedZone = this.view.data.zones.find(
        (zone) => zone.uniqueId === this.view.data.selectedZoneUniqueId
      );
      if (this.view.data.selectedZone)
        this.view.data.selectedZone.selected = true;
    },
    selectedZoneDataChanged() {
      AerialZonesHelper.updateZone(
        this.view.data.zones,
        this.view.data.selectedZone
      );
    },
    verifySelectedZone() {
      this.view.data.selectedZone = this.view.data.zones.find(
        (zone) => zone.selected
      );
      this.view.data.selectedZoneUniqueId = this.view.data.selectedZone
        ? this.view.data.selectedZone.uniqueId
        : null;
    },
    canAddZones() {
      return AerialZonesHelper.canAddZones(this.view.data.zones);
    },
    addZone() {
      AerialZonesHelper.addZone(this.view.data.zones);
    },
    removeZone() {
      AerialZonesHelper.removeZone(
        this.view.data.zones,
        this.view.data.selectedZone
      );
    },
    saveData() {
      if (!AerialZonesHelper.validVertices(this.view.data.zones)) {
        this.$notify({
          title: "Setting Update",
          message:
            "Some zones are out of bounds, please fix them and try to save again",
          type: "error",
        });
        return;
      }

      this.ready = false;

      const payload = {
        doorbotId: this.doorbotId,
        payload: AerialZonesHelper.buildPayloadForSave(
          this.view.data.zones,
          this.doorbotId
        ),
      };

      this.updateDeviceZones(payload)
        .then((data) => {
          this.zoomIn = false;
          this.data = data.zone_settings;
          merge(this.config.value, this.data);
          this.updateDeviceZoneSettings(this.data);
          window.setTimeout(() => this.configPreview.bind(this)(), 100);

          this.$message({
            showClose: true,
            message: "Setting updated",
            type: "success",
          });
        })
        .catch((err) => {
          const h = this.$createElement;
          const serverMessage =
            get(err, "data.error") || "Could not update setting";
          this.$notify({
            title: "Setting Update",
            message: h(
              "i",
              { class: { "update-setting_error_msg": true } },
              serverMessage
            ),
            duration: 0,
          });
        })
        .finally(() => (this.ready = true));
    },
  },
};
</script>

<style lang="scss" scoped>
.el-form-item {
  padding-top: 8px !important;
}
.el-form-item__label {
  padding-bottom: 0px !important;
}
.el-form--label-top .el-form-item__label {
  padding-bottom: 0px !important;
}

.motion-zones-dialog {
  & div.konvajs-content[role="presentation"] {
    background: #f5f5f5 !important;
    border-radius: 10px !important;
  }
  & .motion-zones-legend__container {
    padding: 0;
    list-style: none;
    margin: 0;
    & .on-off-zone-indicator {
      margin-top: 5px;
    }
  }
  & .motion-zones-legend {
    height: 40px;
    margin: 17px;
    border: #4c4c4c solid 1px;
    /*border: solid 1px;*/
    border-radius: 2px;
  }
  & .motion-zones-dialog__content {
    margin-bottom: -6px;
  }

  .motion-zone-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px;
    border: solid 1px #eee;
    margin: 0px 10px 10px;
    border-radius: 5px;
    .motion-zone-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .m-l-10 {
        margin-left: 10px;
      }
      .zone-color {
        width: 40px;
        display: inline-block;
        height: 40px;
        border-radius: 100%;
      }
    }
  }
}
</style>
